/**
 * Notification Card Component
 * 
 * Reusable card for displaying individual notifications.
 * Shows icon, title, message, timestamp, and read/unread indicator.
 */

import { formatDistanceToNow } from "date-fns";
import {
  CheckCircle2,
  AlertTriangle,
  XCircle,
  Info,
  TrendingUp,
  ShieldOff,
  Skull,
  ArrowDownCircle,
  ArrowUpCircle,
  ShieldAlert,
} from "lucide-react";
import type { UserNotification } from "@nordex/core";
import { cn } from "@/lib/utils";

interface NotificationCardProps {
  notification: UserNotification;
  onClick?: () => void;
}

// Get icon based on notification type, severity, and metadata
const getNotificationIcon = (type: string, severity: string, metadata?: Record<string, unknown>) => {
  // Type-specific icons
  switch (type) {
    case "order_filled":
      return CheckCircle2;
    case "tp_sl_triggered":
      // Different icons: TrendingUp for Take Profit, ShieldOff for Stop Loss (protection worked!)
      if (metadata?.trigger_type === "stop_loss") {
        return ShieldOff;
      }
      return TrendingUp;
    case "liquidation":
      return Skull;
    case "deposit":
      return ArrowDownCircle;
    case "withdrawal":
      return ArrowUpCircle;
    case "account_security":
      return ShieldAlert;
    case "near_liquidation":
      return AlertTriangle;
    default:
      // Fallback to severity-based icons
      switch (severity) {
        case "success":
          return CheckCircle2;
        case "warning":
          return AlertTriangle;
        case "error":
          return XCircle;
        default:
          return Info;
      }
  }
};

// Get color classes based on severity
const getSeverityColors = (severity: string) => {
  switch (severity) {
    case "success":
      return {
        icon: "text-bid-foreground",
        bg: "bg-bid/10",
        border: "border-bid/20",
      };
    case "warning":
      return {
        icon: "text-orange-500",
        bg: "bg-orange-500/10",
        border: "border-orange-500/20",
      };
    case "error":
      return {
        icon: "text-ask-foreground",
        bg: "bg-ask/10",
        border: "border-ask/20",
      };
    default:
      return {
        icon: "text-blue-500",
        bg: "bg-blue-500/10",
        border: "border-blue-500/20",
      };
  }
};

export const NotificationCard = ({ notification, onClick }: NotificationCardProps) => {
  const Icon = getNotificationIcon(notification.type, notification.severity, notification.metadata);
  const colors = getSeverityColors(notification.severity);
  
  const timeAgo = formatDistanceToNow(new Date(notification.created_at), {
    addSuffix: true,
  });
  
  return (
    <button
      onClick={onClick}
      className={cn(
        "w-full text-left p-3 rounded-lg border transition-all duration-300",
        "hover:bg-muted/50 active:scale-[0.98]",
        notification.read
          ? "bg-background border-border opacity-70"
          : "bg-muted/20 border-primary/20",
        colors.border
      )}
    >
      <div className="flex items-start gap-3">
        {/* Icon */}
        <div className={cn("flex-shrink-0 mt-0.5", colors.icon)}>
          <Icon className="size-5" />
        </div>
        
        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <h4 className={cn(
              "text-sm font-semibold",
              notification.read ? "text-foreground/80" : "text-foreground"
            )}>
              {notification.title}
            </h4>
            {!notification.read && (
              <div className="flex-shrink-0 h-2 w-2 rounded-full bg-primary mt-1" />
            )}
          </div>
          
          <p className={cn(
            "text-xs mt-1",
            notification.read ? "text-muted-foreground" : "text-foreground/70"
          )}>
            {notification.message}
          </p>
          
          <p className="text-[10px] text-muted-foreground mt-1.5">
            {timeAgo}
          </p>
        </div>
      </div>
    </button>
  );
};

